import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  ChevronDown,
  Cloud,
  Database,
  Download,
  Eye,
  FileClock,
  FileUp,
  Filter,
  RefreshCw,
  Wallet,
} from "lucide-react";
import { getCurrentUser } from "../../../utils/auth";
import {
  exportGcsCostLogs,
  getGcsActualReport,
  getGcsCostLogs,
  getGcsCostSummary,
  getGcsMonthlyReport,
} from "../../../api/settings/gcsCost";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import * as XLSX from "xlsx";

const PIE_COLORS = ["#dc2626", "#f97316", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#6b7280"];

function formatBytes(bytes) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const p = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const v = n / 1024 ** p;
  return `${v.toFixed(v >= 100 || p === 0 ? 0 : 2)} ${units[p]}`;
}

function formatCurrencyUsd(v) {
  const n = Number(v || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(n);
}

function formatApiError(err, fallback) {
  const data = err?.response?.data;
  if (data?.code === "APP_MAINTENANCE") {
    return (
      data.maintenance_message ||
      `${data.title || data.message || "Hệ thống đang bảo trì"}. Chỉ tài khoản administrator@hamesoft.com mới truy cập được API trong thời gian bảo trì.`
    );
  }
  return data?.message || fallback;
}

function isAdminUser() {
  const user = getCurrentUser();
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const normalized = roles.map((r) => String(r || "").trim().toLowerCase());
  return normalized.some((r) =>
    ["administrator", "admin", "super admin", "superadmin"].includes(r)
  );
}

function SegmentedControl({ options, value, onChange }) {
  return (
    <div className="flex w-full rounded-2xl bg-gray-100 p-1 gap-1">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex-1 rounded-xl px-2 py-2 text-[11px] sm:text-xs font-bold uppercase tracking-wide transition ${
              active
                ? "bg-white text-red-700 shadow-sm ring-1 ring-black/5"
                : "text-gray-500"
            }`}
          >
            <span className="sm:hidden">{opt.short || opt.label}</span>
            <span className="hidden sm:inline">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

const MetricCard = ({ icon: Icon, title, value, note }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm sm:p-4">
    <div className="mb-2 flex items-start justify-between gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-700 sm:h-10 sm:w-10">
        <Icon size={16} className="sm:hidden" />
        <Icon size={18} className="hidden sm:block" />
      </div>
    </div>
    <p className="text-[10px] font-bold uppercase leading-snug tracking-wide text-gray-400 sm:text-xs">
      {title}
    </p>
    <p className="mt-1 text-lg font-black leading-tight text-gray-900 sm:text-xl">{value}</p>
    {note ? <p className="mt-1 text-[11px] leading-snug text-gray-500 sm:text-xs">{note}</p> : null}
  </div>
);

function SectionCard({ title, icon: Icon, children, action, className = "" }) {
  return (
    <div className={`rounded-2xl border border-gray-100 bg-white p-3 shadow-sm sm:p-4 ${className}`}>
      {(title || action) && (
        <div className="mb-3 flex items-center justify-between gap-2">
          {title ? (
            <h2 className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wide text-gray-800 sm:text-sm">
              {Icon ? <Icon size={15} className="text-red-600" /> : null}
              {title}
            </h2>
          ) : (
            <span />
          )}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function DesktopTable({ children, minWidth = "640px" }) {
  return (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full text-sm" style={{ minWidth }}>
        {children}
      </table>
    </div>
  );
}

function ProgressRow({ label, meta, valueLabel, percent, color = "#dc2626" }) {
  return (
    <div className="space-y-1.5 py-2.5 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-800">{label}</p>
          {meta ? <p className="text-[11px] text-gray-500">{meta}</p> : null}
        </div>
        <p className="shrink-0 text-sm font-black text-gray-900">{valueLabel}</p>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.max(2, Math.min(100, percent || 0))}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function RankRow({ rank, title, subtitle, primary, chips }) {
  return (
    <div className="flex items-start gap-3 border-b border-gray-50 py-3 last:border-0 last:pb-0 first:pt-0">
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-black ${
          rank === 1
            ? "bg-red-600 text-white"
            : rank === 2
              ? "bg-orange-100 text-orange-700"
              : rank === 3
                ? "bg-amber-100 text-amber-700"
                : "bg-gray-100 text-gray-600"
        }`}
      >
        {rank}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">{title}</p>
            {subtitle ? <p className="text-[11px] text-gray-500">{subtitle}</p> : null}
          </div>
          <p className="shrink-0 text-sm font-black text-red-700">{primary}</p>
        </div>
        {chips?.length ? (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {chips.map((c) => (
              <span
                key={c}
                className="rounded-md bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600"
              >
                {c}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LogMobileItem({ r, actionBadgeClass }) {
  return (
    <div className="border-b border-gray-50 py-3 last:border-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900">{r.file_name}</p>
          <p className="text-[11px] text-gray-500">
            {new Date(r.created_at).toLocaleString("vi-VN")}
          </p>
        </div>
        <span
          className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${actionBadgeClass(
            r.action
          )}`}
        >
          {r.action}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-lg bg-gray-50 px-2 py-1.5">
          <p className="text-gray-400">Người dùng</p>
          <p className="truncate font-semibold text-gray-800">{r.user_name || "—"}</p>
        </div>
        <div className="rounded-lg bg-gray-50 px-2 py-1.5">
          <p className="text-gray-400">Transfer</p>
          <p className="font-semibold text-gray-800">{formatBytes(r.transfer_bytes)}</p>
        </div>
        <div className="rounded-lg bg-gray-50 px-2 py-1.5">
          <p className="text-gray-400">Loại</p>
          <p className="truncate font-semibold text-gray-800">{r.document_type || "khac"}</p>
        </div>
        <div className="rounded-lg bg-gray-50 px-2 py-1.5">
          <p className="text-gray-400">Storage</p>
          <p className="font-semibold text-gray-800">{formatBytes(r.storage_bytes)}</p>
        </div>
      </div>
    </div>
  );
}

function DbSummarySection({ dbData, loading }) {
  const totals = dbData?.totals || {};
  const cost = dbData?.estimatedCost || {};
  const chartDaily = (dbData?.daily || []).map((d) => ({
    ...d,
    day: String(d.day || "").slice(5),
    transferMb: Number(d.transfer_bytes || 0) / (1024 * 1024),
  }));
  const byType = dbData?.byType || [];
  const maxTypeBytes = Math.max(...byType.map((d) => Number(d.storage_bytes || 0)), 1);
  const chartByType = byType.map((d) => ({
    name: d.document_type || "khac",
    value: Number(d.storage_bytes || 0),
  }));
  const topDocs = dbData?.topDocuments || [];
  const maxTransfer = Math.max(...topDocs.map((d) => Number(d.transfer_bytes || 0)), 1);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-3 xl:gap-4">
        <MetricCard
          icon={Database}
          title="Lưu trữ hiện tại"
          value={formatBytes(totals.total_storage_bytes)}
          note={`${totals.total_documents || 0} tài liệu active`}
        />
        <MetricCard
          icon={Download}
          title="Tải / xem kỳ lọc"
          value={formatBytes(totals.transfer_bytes)}
          note={`${totals.view_count || 0} xem · ${totals.download_count || 0} tải`}
        />
        <MetricCard
          icon={Wallet}
          title="Chi phí kỳ lọc"
          value={formatCurrencyUsd(cost.transferCostInPeriod)}
          note={`Egress ${formatCurrencyUsd(cost.egressRatePerGb)}/GB`}
        />
        <MetricCard
          icon={FileUp}
          title="Upload / Update"
          value={`${totals.upload_count || 0} / ${totals.update_count || 0}`}
          note={`+${formatBytes(totals.storage_bytes_logged)} storage`}
        />
        <MetricCard
          icon={Database}
          title="Storage / tháng"
          value={formatCurrencyUsd(cost.storageCostPerMonth)}
          note={`${formatCurrencyUsd(cost.storageRatePerGbMonth)}/GB-tháng`}
        />
        <MetricCard
          icon={Eye}
          title="Lượt truy cập"
          value={`${(totals.view_count || 0) + (totals.download_count || 0)}`}
          note="Xem trong app + tải file"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4">
        <SectionCard title="Daily transfer (MB)">
          <div className="h-48 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartDaily} margin={{ left: -18, right: 4, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} width={34} />
                <Tooltip
                  formatter={(val) => [`${Number(val).toFixed(2)} MB`, "Transfer"]}
                  labelFormatter={(label) => `Ngày ${label}`}
                />
                <Line
                  type="monotone"
                  dataKey="transferMb"
                  stroke="#dc2626"
                  strokeWidth={2.5}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Tỷ trọng dung lượng">
          <div className="h-48 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartByType}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="46%"
                  outerRadius={68}
                  innerRadius={34}
                  label={false}
                >
                  {chartByType.map((_, idx) => (
                    <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [formatBytes(v), "Dung lượng"]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4">
        <SectionCard title="Theo phân loại tài liệu">
          <div className="md:hidden divide-y divide-gray-50">
            {byType.map((r, idx) => (
              <ProgressRow
                key={r.document_type}
                label={r.document_type || "khac"}
                meta={`${r.document_count} tài liệu`}
                valueLabel={formatBytes(r.storage_bytes)}
                percent={(Number(r.storage_bytes || 0) / maxTypeBytes) * 100}
                color={PIE_COLORS[idx % PIE_COLORS.length]}
              />
            ))}
            {!loading && byType.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">Chưa có dữ liệu.</p>
            ) : null}
          </div>
          <DesktopTable>
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-2">Loại</th>
                <th className="py-2 pr-2">Số lượng</th>
                <th className="py-2">Dung lượng</th>
              </tr>
            </thead>
            <tbody>
              {byType.map((r) => (
                <tr key={r.document_type} className="border-t border-gray-100">
                  <td className="py-2 pr-2 font-semibold text-gray-800">{r.document_type || "khac"}</td>
                  <td className="py-2 pr-2">{r.document_count}</td>
                  <td className="py-2">{formatBytes(r.storage_bytes)}</td>
                </tr>
              ))}
            </tbody>
          </DesktopTable>
        </SectionCard>

        <SectionCard title="Top tài liệu tốn băng thông">
          <div className="md:hidden">
            {topDocs.map((r, idx) => (
              <RankRow
                key={`${r.document_id || "deleted"}-${idx}`}
                rank={idx + 1}
                title={r.file_name}
                subtitle={r.document_type || "khac"}
                primary={formatBytes(r.transfer_bytes)}
                chips={[`View ${r.view_count}`, `DL ${r.download_count}`]}
              />
            ))}
            {!loading && topDocs.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">Chưa có dữ liệu.</p>
            ) : null}
            {topDocs.length > 0 ? (
              <div className="mt-1 space-y-2 border-t border-gray-50 pt-3">
                {topDocs.slice(0, 5).map((r, idx) => (
                  <div key={`bar-${idx}`} className="flex items-center gap-2">
                    <span className="w-4 text-[10px] font-bold text-gray-400">{idx + 1}</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-red-500"
                        style={{
                          width: `${(Number(r.transfer_bytes || 0) / maxTransfer) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <DesktopTable minWidth="520px">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-2">Tài liệu</th>
                <th className="py-2 pr-2">View</th>
                <th className="py-2 pr-2">Download</th>
                <th className="py-2">Transfer</th>
              </tr>
            </thead>
            <tbody>
              {topDocs.map((r, idx) => (
                <tr key={`${r.document_id || "deleted"}-${idx}`} className="border-t border-gray-100">
                  <td className="py-2 pr-2">
                    <p className="max-w-[220px] truncate font-semibold text-gray-800" title={r.file_name}>
                      {r.file_name}
                    </p>
                    <p className="text-xs text-gray-500">{r.document_type || "khac"}</p>
                  </td>
                  <td className="py-2 pr-2">{r.view_count}</td>
                  <td className="py-2 pr-2">{r.download_count}</td>
                  <td className="py-2">{formatBytes(r.transfer_bytes)}</td>
                </tr>
              ))}
            </tbody>
          </DesktopTable>
        </SectionCard>
      </div>
      {loading ? <p className="text-sm text-gray-500">Đang tải dữ liệu...</p> : null}
    </div>
  );
}

function DbLogsSection({ logs, logsTotal, logsPage, logsAction, loadLogs, loading }) {
  const actionBadgeClass = (action) => {
    const a = String(action || "").toLowerCase();
    if (a === "upload") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (a === "update") return "bg-amber-50 text-amber-700 border-amber-200";
    if (a === "download") return "bg-red-50 text-red-700 border-red-200";
    return "bg-blue-50 text-blue-700 border-blue-200";
  };

  return (
    <SectionCard
      title="Nhật ký chi tiết"
      icon={FileClock}
      action={
        <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold text-gray-600">
          {logsTotal} bản ghi
        </span>
      }
    >
      <div className="mb-2 flex flex-wrap gap-1.5">
        <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
          {logsAction || "all"}
        </span>
      </div>

      <div className="md:hidden">
        {logs.map((r) => (
          <LogMobileItem key={r.id} r={r} actionBadgeClass={actionBadgeClass} />
        ))}
        {!loading && logs.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            Không có dữ liệu nhật ký theo điều kiện lọc hiện tại.
          </p>
        ) : null}
      </div>

      <DesktopTable minWidth="960px">
        <thead className="bg-gray-50">
          <tr className="text-left text-gray-500">
            <th className="py-2 pr-2">Thời gian</th>
            <th className="py-2 pr-2">Action</th>
            <th className="py-2 pr-2">Tài liệu</th>
            <th className="py-2 pr-2">Loại</th>
            <th className="py-2 pr-2">Người dùng</th>
            <th className="py-2 pr-2">Khóa học</th>
            <th className="py-2 pr-2">Storage</th>
            <th className="py-2">Transfer</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((r) => (
            <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/70">
              <td className="whitespace-nowrap py-2 pr-2">{new Date(r.created_at).toLocaleString("vi-VN")}</td>
              <td className="py-2 pr-2">
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-1 text-[11px] font-bold uppercase ${actionBadgeClass(
                    r.action
                  )}`}
                >
                  {r.action}
                </span>
              </td>
              <td className="py-2 pr-2">
                <p className="max-w-[260px] truncate font-medium text-gray-800" title={r.file_name}>
                  {r.file_name}
                </p>
              </td>
              <td className="py-2 pr-2">{r.document_type || "khac"}</td>
              <td className="py-2 pr-2">
                <p>{r.user_name || "—"}</p>
                <p className="text-xs text-gray-500">{r.user_email || ""}</p>
              </td>
              <td className="py-2 pr-2">{r.course_name || "—"}</td>
              <td className="py-2 pr-2">{formatBytes(r.storage_bytes)}</td>
              <td className="py-2">{formatBytes(r.transfer_bytes)}</td>
            </tr>
          ))}
          {!loading && logs.length === 0 ? (
            <tr>
              <td colSpan={8} className="py-8 text-center text-sm text-gray-500">
                Không có dữ liệu nhật ký theo điều kiện lọc hiện tại.
              </td>
            </tr>
          ) : null}
        </tbody>
      </DesktopTable>

      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="text-xs text-gray-500">Trang {logsPage}</p>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={logsPage <= 1}
            onClick={() => loadLogs(logsPage - 1)}
            className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold disabled:opacity-40"
          >
            Trước
          </button>
          <button
            type="button"
            disabled={logsPage * 20 >= logsTotal}
            onClick={() => loadLogs(logsPage + 1)}
            className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold disabled:opacity-40"
          >
            Sau
          </button>
        </div>
      </div>
      {loading ? <p className="mt-2 text-sm text-gray-500">Đang tải nhật ký...</p> : null}
    </SectionCard>
  );
}

function GcsActualSection({ gcsData, loading }) {
  const totals = gcsData?.totals || {};
  const bucketTotals = gcsData?.bucketTotals || {};
  const buckets = gcsData?.buckets || [];
  const maxBucketBytes = Math.max(...buckets.map((b) => Number(b.total_bytes || 0)), 1);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4 xl:gap-4">
        <MetricCard
          icon={Cloud}
          title="Bucket đang quét"
          value={`${bucketTotals.bucket_count || buckets.length || 0}`}
          note={
            gcsData?.snapshotAt
              ? new Date(gcsData.snapshotAt).toLocaleString("vi-VN")
              : "ima-documents · imavn"
          }
        />
        <MetricCard
          icon={Database}
          title="Tổng object"
          value={`${bucketTotals.object_count || 0}`}
          note={`${bucketTotals.ok_bucket_count || 0} OK · ${bucketTotals.failed_bucket_count || 0} lỗi`}
        />
        <MetricCard
          icon={Wallet}
          title="Dung lượng GCS"
          value={formatBytes(bucketTotals.total_bytes)}
          note="Inventory thực tế"
        />
        <MetricCard
          icon={AlertTriangle}
          title="Đối soát DB"
          value={`${totals.total_documents || 0}`}
          note={`${totals.found_in_gcs || 0} khớp · ${totals.missing_in_gcs || 0} thiếu`}
        />
      </div>

      <SectionCard title="Số liệu theo bucket">
        <div className="md:hidden divide-y divide-gray-50">
          {buckets.map((b, idx) => (
            <div key={b.bucketName} className="py-3 first:pt-0 last:pb-0">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">{b.bucketName}</p>
                  <p className="text-[11px] text-gray-500">
                    {b.object_count || 0} object
                    {b.ok && b.scanned_at
                      ? ` · ${new Date(b.scanned_at).toLocaleString("vi-VN")}`
                      : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-gray-900">{formatBytes(b.total_bytes)}</p>
                  <span
                    className={`inline-flex rounded-full border px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                      b.ok
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-red-200 bg-red-50 text-red-700"
                    }`}
                  >
                    {b.ok ? "OK" : "Lỗi"}
                  </span>
                </div>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(Number(b.total_bytes || 0) / maxBucketBytes) * 100}%`,
                    backgroundColor: PIE_COLORS[idx % PIE_COLORS.length],
                  }}
                />
              </div>
              {!b.ok ? <p className="mt-1 text-[11px] text-red-600">{b.error}</p> : null}
            </div>
          ))}
        </div>
        <DesktopTable minWidth="720px">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 pr-2">Bucket</th>
              <th className="py-2 pr-2">Trạng thái</th>
              <th className="py-2 pr-2">Số object</th>
              <th className="py-2 pr-2">Dung lượng</th>
              <th className="py-2">Ghi chú</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((b) => (
              <tr key={b.bucketName} className="border-t border-gray-100">
                <td className="py-2 pr-2 font-semibold text-gray-800">{b.bucketName}</td>
                <td className="py-2 pr-2">{b.ok ? "OK" : "Lỗi"}</td>
                <td className="py-2 pr-2">{b.object_count || 0}</td>
                <td className="py-2 pr-2">{formatBytes(b.total_bytes)}</td>
                <td className="py-2 text-xs text-gray-500">
                  {b.ok
                    ? b.scanned_at
                      ? `Quét lúc ${new Date(b.scanned_at).toLocaleString("vi-VN")}`
                      : "—"
                    : b.error || "Không đọc được bucket"}
                </td>
              </tr>
            ))}
          </tbody>
        </DesktopTable>
      </SectionCard>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4">
        <SectionCard title="Đối soát theo loại">
          <div className="md:hidden divide-y divide-gray-50">
            {(gcsData?.byType || []).map((r) => (
              <div key={r.document_type} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-900">{r.document_type || "khac"}</p>
                  <p className="text-xs font-bold text-gray-500">{r.document_count} file</p>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                  <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                    <p className="text-gray-400">DB</p>
                    <p className="font-semibold text-gray-800">{formatBytes(r.db_bytes)}</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                    <p className="text-gray-400">GCS</p>
                    <p className="font-semibold text-gray-800">{formatBytes(r.gcs_bytes)}</p>
                  </div>
                </div>
                <p className="mt-1.5 text-[11px] text-amber-700">
                  {r.missing_in_gcs} thiếu · {r.mismatched_size_count} lệch
                </p>
              </div>
            ))}
          </div>
          <DesktopTable minWidth="560px">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-2">Loại</th>
                <th className="py-2 pr-2">Số lượng</th>
                <th className="py-2 pr-2">DB</th>
                <th className="py-2 pr-2">GCS</th>
                <th className="py-2">Thiếu / lệch</th>
              </tr>
            </thead>
            <tbody>
              {(gcsData?.byType || []).map((r) => (
                <tr key={r.document_type} className="border-t border-gray-100">
                  <td className="py-2 pr-2 font-semibold text-gray-800">{r.document_type || "khac"}</td>
                  <td className="py-2 pr-2">{r.document_count}</td>
                  <td className="py-2 pr-2">{formatBytes(r.db_bytes)}</td>
                  <td className="py-2 pr-2">{formatBytes(r.gcs_bytes)}</td>
                  <td className="py-2">
                    {r.missing_in_gcs} thiếu · {r.mismatched_size_count} lệch
                  </td>
                </tr>
              ))}
            </tbody>
          </DesktopTable>
        </SectionCard>

        <SectionCard title="Thiếu / lệch size">
          <div className="md:hidden">
            {(gcsData?.mismatches || []).map((r, idx) => (
              <RankRow
                key={r.id}
                rank={idx + 1}
                title={r.file_name}
                subtitle={r.document_type || "khac"}
                primary={
                  r.gcs_found
                    ? `±${formatBytes(Math.abs(Number(r.size_delta || 0)))}`
                    : "Missing"
                }
                chips={[`DB ${formatBytes(r.file_size)}`, `GCS ${formatBytes(r.gcs_size)}`]}
              />
            ))}
            {!loading && (gcsData?.mismatches || []).length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">Không phát hiện lệch size.</p>
            ) : null}
          </div>
          <DesktopTable minWidth="560px">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-2">Tài liệu</th>
                <th className="py-2 pr-2">Loại</th>
                <th className="py-2 pr-2">DB</th>
                <th className="py-2 pr-2">GCS</th>
                <th className="py-2">Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {(gcsData?.mismatches || []).map((r) => (
                <tr key={r.id} className="border-t border-gray-100">
                  <td className="py-2 pr-2">
                    <p className="max-w-[260px] truncate font-semibold text-gray-800" title={r.file_name}>
                      {r.file_name}
                    </p>
                    <p className="text-xs text-gray-500">{r.course_name || "—"}</p>
                  </td>
                  <td className="py-2 pr-2">{r.document_type || "khac"}</td>
                  <td className="py-2 pr-2">{formatBytes(r.file_size)}</td>
                  <td className="py-2 pr-2">{formatBytes(r.gcs_size)}</td>
                  <td className="py-2">
                    {r.gcs_found
                      ? `Lệch ${formatBytes(Math.abs(Number(r.size_delta || 0)))}`
                      : "Không tìm thấy trên GCS"}
                  </td>
                </tr>
              ))}
            </tbody>
          </DesktopTable>
        </SectionCard>
      </div>
      {loading ? <p className="text-sm text-gray-500">Đang tải báo cáo GCS...</p> : null}
    </div>
  );
}

function GcsMonthlySection({ monthlyData, loading }) {
  if (!monthlyData) return loading ? <p className="text-sm text-gray-500">Đang tải báo cáo tháng...</p> : null;

  const { totals, buckets, finalized, source, billing_configured, monitoring_ok, period } = monthlyData;

  return (
    <div className="space-y-3 sm:space-y-4">
      {!billing_configured && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <strong>Billing Export chưa cấu hình.</strong> Chi phí sẽ là $0 cho đến khi bật Detailed Billing Export và điền BQ_BILLING_*.
        </div>
      )}
      {!monitoring_ok && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <strong>Monitoring thiếu dữ liệu</strong> cho tháng {period}.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase ${
            finalized
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-blue-200 bg-blue-50 text-blue-700"
          }`}
        >
          {finalized ? "Đã chốt" : "Live"}
        </span>
        <span className="text-xs text-gray-500">
          {period} · nguồn {source}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4 xl:gap-4">
        <MetricCard
          icon={ArrowUpFromLine}
          title="Upload"
          value={formatBytes(totals.ingress_bytes)}
          note={`Tháng ${period}`}
        />
        <MetricCard
          icon={ArrowDownToLine}
          title="Download"
          value={formatBytes(totals.egress_bytes)}
          note={`Tháng ${period}`}
        />
        <MetricCard
          icon={Wallet}
          title="Gross"
          value={formatCurrencyUsd(totals.cost)}
          note={`Credits ${formatCurrencyUsd(totals.credits)}`}
        />
        <MetricCard
          icon={Wallet}
          title="Net"
          value={formatCurrencyUsd(totals.net_cost)}
          note={`${(totals.request_count || 0).toLocaleString()} requests`}
        />
      </div>

      <SectionCard title="Chi tiết theo bucket">
        <div className="md:hidden space-y-2">
          {(buckets || []).map((b) => (
            <div key={b.bucket_name} className="rounded-xl border border-gray-100 bg-gray-50/70 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900">{b.bucket_name}</p>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${
                    b.finalized
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-blue-200 bg-blue-50 text-blue-700"
                  }`}
                >
                  {b.source || (b.finalized ? "cache" : "live")}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded-lg bg-white px-2 py-1.5">
                  <p className="text-gray-400">Ingress</p>
                  <p className="font-semibold">{formatBytes(b.ingress_bytes)}</p>
                </div>
                <div className="rounded-lg bg-white px-2 py-1.5">
                  <p className="text-gray-400">Egress</p>
                  <p className="font-semibold">{formatBytes(b.egress_bytes)}</p>
                </div>
                <div className="rounded-lg bg-white px-2 py-1.5">
                  <p className="text-gray-400">Cost</p>
                  <p className="font-semibold">{formatCurrencyUsd(b.cost)}</p>
                </div>
                <div className="rounded-lg bg-white px-2 py-1.5">
                  <p className="text-gray-400">Net</p>
                  <p className="font-semibold">{formatCurrencyUsd(b.net_cost)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <DesktopTable minWidth="920px">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 pr-2">Bucket</th>
              <th className="py-2 pr-2">Ingress</th>
              <th className="py-2 pr-2">Egress</th>
              <th className="py-2 pr-2">Requests</th>
              <th className="py-2 pr-2">Cost</th>
              <th className="py-2 pr-2">Credits</th>
              <th className="py-2 pr-2">Net</th>
              <th className="py-2">Nguồn</th>
            </tr>
          </thead>
          <tbody>
            {(buckets || []).map((b) => (
              <tr key={b.bucket_name} className="border-t border-gray-100">
                <td className="py-2 pr-2 font-semibold text-gray-800">{b.bucket_name}</td>
                <td className="py-2 pr-2">{formatBytes(b.ingress_bytes)}</td>
                <td className="py-2 pr-2">{formatBytes(b.egress_bytes)}</td>
                <td className="py-2 pr-2">{(Number(b.request_count) || 0).toLocaleString()}</td>
                <td className="py-2 pr-2">{formatCurrencyUsd(b.cost)}</td>
                <td className="py-2 pr-2">{formatCurrencyUsd(b.credits)}</td>
                <td className="py-2 pr-2">{formatCurrencyUsd(b.net_cost)}</td>
                <td className="py-2">{b.source || (b.finalized ? "cache" : "live")}</td>
              </tr>
            ))}
          </tbody>
        </DesktopTable>
      </SectionCard>
      {loading && <p className="text-sm text-gray-500">Đang tải...</p>}
    </div>
  );
}

export default function GcsCostDashboardPage() {
  const [sourceTab, setSourceTab] = useState("db");
  const [dbTab, setDbTab] = useState("summary");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dbData, setDbData] = useState(null);
  const [gcsData, setGcsData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsAction, setLogsAction] = useState("");
  const [exporting, setExporting] = useState(false);

  const isAdmin = useMemo(() => isAdminUser(), []);

  const filterSummary = useMemo(() => {
    if (sourceTab === "gcs") return `Tháng ${selectedMonth}`;
    if (from || to) return `${from || "..."} → ${to || "..."}`;
    return "Tất cả thời gian";
  }, [sourceTab, selectedMonth, from, to]);

  const loadSummary = async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError("");
    try {
      const res = await getGcsCostSummary({ from, to });
      setDbData(res);
    } catch (err) {
      setError(formatApiError(err, "Không tải được dashboard."));
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async (nextPage = logsPage) => {
    if (!isAdmin) return;
    setLoading(true);
    setError("");
    try {
      const res = await getGcsCostLogs({
        from,
        to,
        action: logsAction,
        page: nextPage,
        size: 20,
      });
      setLogs(res.records || []);
      setLogsTotal(Number(res.total || 0));
      setLogsPage(Number(res.page || nextPage));
    } catch (err) {
      setError(formatApiError(err, "Không tải được nhật ký."));
    } finally {
      setLoading(false);
    }
  };

  const loadGcsActual = async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError("");
    try {
      const res = await getGcsActualReport();
      setGcsData(res);
    } catch (err) {
      setError(formatApiError(err, "Không tải được báo cáo GCS."));
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyReport = async (month = selectedMonth) => {
    if (!isAdmin) return;
    setLoading(true);
    setError("");
    try {
      const res = await getGcsMonthlyReport(month);
      setMonthlyData(res);
    } catch (err) {
      setError(formatApiError(err, "Không tải được báo cáo chi phí theo tháng."));
    } finally {
      setLoading(false);
    }
  };

  const exportLogsXlsx = async () => {
    setExporting(true);
    setError("");
    try {
      const res = await exportGcsCostLogs({ from, to, action: logsAction, limit: 50000 });
      const rows = (res.records || []).map((r) => ({
        "Thời gian": new Date(r.created_at).toLocaleString("vi-VN"),
        Action: String(r.action || "").toUpperCase(),
        "Tài liệu": r.file_name || "",
        "Loại tài liệu": r.document_type || "",
        "Người dùng": r.user_name || "",
        Email: r.user_email || "",
        "Khóa học": r.course_name || "",
        "Storage (bytes)": Number(r.storage_bytes || 0),
        "Transfer (bytes)": Number(r.transfer_bytes || 0),
        "Storage hiển thị": formatBytes(r.storage_bytes),
        "Transfer hiển thị": formatBytes(r.transfer_bytes),
        "Nguồn module": r.source_module || "",
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [
        { wch: 21 }, { wch: 10 }, { wch: 42 }, { wch: 16 }, { wch: 24 }, { wch: 30 },
        { wch: 28 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
      ];
      XLSX.utils.book_append_sheet(wb, ws, "Logs");
      const suffix = `${from || "all"}_${to || "all"}_${logsAction || "all"}`;
      XLSX.writeFile(wb, `gcs-logs_${suffix}.xlsx`);
    } catch (err) {
      setError(err?.response?.data?.message || "Export thất bại.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (sourceTab === "gcs") {
      loadGcsActual();
      loadMonthlyReport(selectedMonth);
    } else if (dbTab === "summary") loadSummary();
    else loadLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, sourceTab, dbTab]);

  if (!isAdmin) {
    return (
      <div className="rounded-2xl border border-red-100 bg-white p-4 sm:p-6">
        <h1 className="text-xl font-black text-red-700">GCS management</h1>
        <p className="mt-2 text-sm text-gray-600">Chỉ admin mới được truy cập màn hình này.</p>
      </div>
    );
  }

  const refreshActiveTab = () => {
    if (sourceTab === "gcs") {
      loadGcsActual();
      loadMonthlyReport(selectedMonth);
    } else if (dbTab === "summary") loadSummary();
    else loadLogs(1);
  };

  return (
    <div className="mx-auto w-full max-w-7xl space-y-3 pb-24 sm:space-y-4 sm:pb-8">
      <div className="flex items-start justify-between gap-3 px-0.5">
        <div className="min-w-0">
          <div className="mb-1 inline-flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-50 text-red-700">
              <Cloud size={16} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
              Settings
            </p>
          </div>
          <h1 className="text-xl font-black tracking-tight text-gray-900 sm:text-2xl">
            GCS management
          </h1>
          <p className="mt-0.5 text-xs text-gray-500 sm:text-sm">
            Theo dõi dung lượng, lưu lượng và chi phí storage
          </p>
        </div>
        <button
          type="button"
          onClick={refreshActiveTab}
          disabled={loading}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm disabled:opacity-50"
          aria-label="Làm mới"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm sm:p-4">
        <SegmentedControl
          value={sourceTab}
          onChange={setSourceTab}
          options={[
            { value: "db", short: "Từ DB", label: "Báo cáo từ DB" },
            { value: "gcs", short: "Từ GCS", label: "Báo cáo từ GCS" },
          ]}
        />

        {sourceTab === "db" ? (
          <div className="mt-2">
            <SegmentedControl
              value={dbTab}
              onChange={setDbTab}
              options={[
                { value: "summary", short: "Tổng quan", label: "Tổng quan chi phí" },
                { value: "logs", short: "Nhật ký", label: "Nhật ký sử dụng" },
              ]}
            />
          </div>
        ) : null}

        <div className="mt-3">
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-left"
          >
            <span className="inline-flex min-w-0 items-center gap-2">
              <Filter size={14} className="shrink-0 text-red-600" />
              <span className="truncate text-xs font-semibold text-gray-700">{filterSummary}</span>
            </span>
            <ChevronDown
              size={16}
              className={`shrink-0 text-gray-400 transition ${filterOpen ? "rotate-180" : ""}`}
            />
          </button>

          {filterOpen ? (
            <div className="mt-3 space-y-3 rounded-xl border border-gray-100 bg-gray-50/80 p-3">
              {sourceTab === "db" ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] font-bold uppercase text-gray-400">Từ ngày</label>
                    <input
                      type="date"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-gray-400">Đến ngày</label>
                    <input
                      type="date"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-sm"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-[10px] font-bold uppercase text-gray-400">Tháng</label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-sm sm:w-56"
                  />
                </div>
              )}

              {sourceTab === "db" && dbTab === "logs" ? (
                <div>
                  <label className="text-[10px] font-bold uppercase text-gray-400">Action</label>
                  <select
                    value={logsAction}
                    onChange={(e) => setLogsAction(e.target.value)}
                    className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-sm"
                  >
                    <option value="">Tất cả</option>
                    <option value="upload">Upload</option>
                    <option value="update">Update</option>
                    <option value="view">View</option>
                    <option value="download">Download</option>
                  </select>
                </div>
              ) : null}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    refreshActiveTab();
                    setFilterOpen(false);
                  }}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-700 px-3 py-2.5 text-sm font-bold text-white"
                >
                  <RefreshCw size={14} />
                  Áp dụng
                </button>
                {sourceTab === "db" && dbTab === "logs" ? (
                  <button
                    type="button"
                    onClick={exportLogsXlsx}
                    disabled={exporting}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-bold text-gray-700 disabled:opacity-50"
                  >
                    <Download size={14} />
                    {exporting ? "..." : "XLSX"}
                  </button>
                ) : null}
              </div>

              {sourceTab === "gcs" ? (
                <p className="text-[11px] leading-relaxed text-gray-500">
                  Chi phí từ BigQuery Billing + lưu lượng Cloud Monitoring cho{" "}
                  <span className="font-semibold text-gray-700">ima-documents</span> và{" "}
                  <span className="font-semibold text-gray-700">imavn</span>.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      </div>

      {sourceTab === "db" && dbTab === "summary" ? (
        <DbSummarySection dbData={dbData} loading={loading} />
      ) : null}
      {sourceTab === "db" && dbTab === "logs" ? (
        <DbLogsSection
          logs={logs}
          logsTotal={logsTotal}
          logsPage={logsPage}
          logsAction={logsAction}
          loadLogs={loadLogs}
          loading={loading}
        />
      ) : null}
      {sourceTab === "gcs" ? (
        <>
          <GcsMonthlySection monthlyData={monthlyData} loading={loading} />
          <GcsActualSection gcsData={gcsData} loading={loading} />
        </>
      ) : null}
    </div>
  );
}
